import "core-js/stable";

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import moment from "moment";
import "moment/locale/ko";
import "moment/locale/ja";
import "moment/locale/zh-cn";
import "moment/locale/pt-br";

import { getLocalLngList, getLocalResource, getDefaultLanguage } from "../../modules/localization";

// 사용자가 화면에서 직접 고른 언어. 관리자 기본 언어는 여기 저장하지 않는다(매 진입마다 settings를 따르도록).
export const I18N_LNG_KEY = "i18nLng";

// 콘솔 언어 코드 → moment locale ID (moment는 zh/pt-BR 코드를 인식하지 못한다)
const MOMENT_LOCALE_BY_LANGUAGE = {
    en: "en",
    ko: "ko",
    ja: "ja",
    zh: "zh-cn",
    "pt-BR": "pt-br",
};

/**
 * 선택된 콘솔 언어에 맞춰 moment 전역 locale을 설정한다.
 * 날짜/시간 포맷(LT, LTS, MMMM, dddd 등)은 번역 리소스가 아닌 이 locale이 결정한다.
 * @param {string} language 콘솔 언어 코드 (en | ko | ja | zh | pt-BR)
 */
export function applyMomentLocale(language) {
    moment.locale(MOMENT_LOCALE_BY_LANGUAGE[language] ?? "en");
}

i18n.use(initReactI18next).init({
    resources: {},
    debug: false,

    fallbackLng: ["en", "ja", "ko", "zh", "pt-BR"],

    nsSeparator: "::",
    keySeparator: "::",

    react: {
        useSuspense: false,
        transKeepBasicHtmlNodesFor: ["br", "strong", "i", "p", "span"],
    },

    interpolation: {
        escapeValue: false, // react already safes from xss
    },
});

// 언어가 바뀌면 moment locale도 함께 동기화한다.
i18n.on("languageChanged", applyMomentLocale);

export async function i18init() {
    let i18nLng = localStorage.getItem(I18N_LNG_KEY);

    // 코드에서 정의한 선택 가능 언어 목록을 항상 최신화 (신규 언어가 즉시 노출되도록)
    const { locales } = getLocalLngList(getDefaultLanguage());
    localStorage.setItem("i18nResources", JSON.stringify(locales));

    if (!i18nLng) {
        i18nLng = getDefaultLanguage();
    }

    const languages = getLocalResource(i18nLng);
    i18n.addResourceBundle(i18nLng, "translation", languages, true, true);

    // 선택한 언어를 실제 활성 언어로 설정한다.
    await i18n.changeLanguage(i18nLng);
    applyMomentLocale(i18nLng);
}

/**
 * 현재 적용된 언어 코드
 *
 * 사용자가 직접 고른 언어와 관리자 기본 언어가 모두 i18n에 반영되므로,
 * 화면에서 "지금 언어"가 필요하면 localStorage 대신 이 값을 쓴다.
 *
 * @returns {string} 언어 코드 (en | ko | ja | zh | pt-BR)
 */
export function getCurrentLanguage() {
    return i18n.language || getDefaultLanguage();
}

/**
 * 번들을 등록하고 언어를 전환한다 (리로드 없음. moment locale은 languageChanged 리스너가 따라온다)
 *
 * @param {string} code 언어 코드
 */
async function applyLanguage(code) {
    i18n.addResourceBundle(code, "translation", getLocalResource(code), true, true);
    await i18n.changeLanguage(code);
}

/**
 * 관리자 기본 언어(settings General consoleDefaultLanguage) 적용
 *
 * 사용자가 직접 고른 언어가 있으면 그 선택을 존중해 아무것도 하지 않는다.
 * 관리자 설정 변경이 매 진입마다 반영되도록 적용 결과를 저장하지 않는다.
 *
 * @param {string} [code] 언어 코드
 */
export async function applyDefaultLanguage(code) {
    if (!code) return;
    if (localStorage.getItem(I18N_LNG_KEY)) return;
    if (i18n.language === code) return;

    await applyLanguage(code);
}

export default i18n;
